import type { ReactNode } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Select, DatePicker } from 'antd';
import dayjs from 'dayjs';
import Breadcrumbs from '../../components/Breadcrumbs';
import { ArrowLeftIcon, CreateTaskIcon, ImagePlaceholderIcon } from '../../assets/icons';

const FIELD =
  'w-full rounded-lg border border-transparent px-4 text-sm text-ink placeholder:text-[#94a3b8] transition-colors focus:outline-none focus:ring-2 focus:ring-brand/15';
const TEXTAREA =
  'w-full resize-none rounded-lg border border-transparent px-4 py-3 text-sm text-ink placeholder:text-[#94a3b8] transition-colors focus:outline-none focus:ring-2 focus:ring-brand/15';

const COORDINATORS = ['Dr. Anita Rao', 'Siddharth V.', 'Meera Desai', 'K. P. Raghav'];
const CATEGORIES = ['Health', 'Cultural', 'Media', 'Campaign'];
const STATUSES = [
  { value: 'planning', label: 'Planning' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'inprogress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
];

interface TaskFormValues {
  name: string;
  coordinator: string;
  category: string;
  status: string;
  dueDate: string;
  description: string;
}

// Validation wired but errors intentionally not surfaced ("hide validations").
const validationSchema = Yup.object({
  name: Yup.string().required('Task name is required'),
});

function Label({ children }: { children: ReactNode }) {
  return <span className="text-sm font-medium text-ink">{children}</span>;
}

export default function CreateTaskPage({ variant = 'task' }: { variant?: 'task' | 'subtask' }) {
  const navigate = useNavigate();
  const { id, taskId } = useParams();
  const location = useLocation();
  const state = location.state as { name?: string; taskName?: string } | null;
  const engagementName = state?.name ?? 'Guru Poornima';
  const taskName = state?.taskName ?? 'Blood Donation Camp';
  const isSub = variant === 'subtask';

  const engagementPath = `/user/engagements/${id ?? ''}`;
  const taskPath = `/user/engagements/${id ?? ''}/tasks/${taskId ?? ''}`;
  // Where the back arrow / cancel / submit return to.
  const backPath = isSub ? taskPath : engagementPath;
  const backState = isSub ? { name: engagementName, taskName } : { name: engagementName };

  const title = isSub ? 'Sub New Task' : 'New Task';
  const submitLabel = isSub ? 'CREATE SUB TASK' : 'CREATE TASK';

  const breadcrumbs = isSub
    ? [
        { label: 'Engagements', to: '/user/engagements' },
        { label: engagementName, to: engagementPath },
        { label: taskName, to: taskPath },
        { label: 'Sub New Task' },
      ]
    : [
        { label: 'Engagements', to: '/user/engagements' },
        { label: engagementName, to: engagementPath },
        { label: 'Create New Task' },
      ];

  const formik = useFormik<TaskFormValues>({
    initialValues: {
      name: '',
      coordinator: '',
      category: '',
      status: 'planning',
      dueDate: '',
      description: '',
    },
    validationSchema,
    onSubmit: (values) => {
      // TODO: wire to a createTask / createSubTask mutation once the API exists.
      // eslint-disable-next-line no-console
      console.log(isSub ? 'Create sub task' : 'Create task', values);
      navigate(backPath, { state: backState });
    },
  });

  const { values, handleChange, handleBlur, handleSubmit, setFieldValue, setFieldTouched } = formik;

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-8">
      {/* Breadcrumb */}
      <Breadcrumbs items={breadcrumbs} />

      {/* Heading */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Back"
            onClick={() => navigate(backPath, { state: backState })}
            className="text-ink transition-colors hover:text-brand"
          >
            <ArrowLeftIcon className="size-7" />
          </button>
          <h1 className="text-2xl font-semibold text-ink">{title}</h1>
        </div>
        <p className="text-base text-ink-secondary">
          Configure details, assign coordinators, and set timelines for the upcoming engagement.
        </p>
      </div>

      {/* Form card */}
      <div className="flex flex-col gap-6 rounded-xl border border-[#f1f5f9] bg-white p-8 shadow-card">
        {/* Task Name (full width) */}
        <label className="flex flex-col gap-1.5">
          <Label>Task Name</Label>
          <input
            name="name"
            placeholder="e.g., Venue finalization and seating arrangement"
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            className={FIELD}
          />
        </label>

        {/* Category | Status */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label>Category</Label>
            <Select
              className="w-full"
              placeholder="Select Category"
              value={values.category || undefined}
              onChange={(v) => setFieldValue('category', v)}
              options={CATEGORIES.map((c) => ({ value: c, label: c }))}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Status</Label>
            <Select
              className="w-full"
              value={values.status}
              onChange={(v) => setFieldValue('status', v)}
              options={STATUSES}
            />
          </div>
        </div>

        {/* Assigned Coordinator | Due Date */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label>Assigned Coordinator</Label>
            <Select
              className="w-full"
              placeholder="Select Coordinator"
              value={values.coordinator || undefined}
              onChange={(v) => setFieldValue('coordinator', v)}
              onBlur={() => setFieldTouched('coordinator', true)}
              options={COORDINATORS.map((c) => ({ value: c, label: c }))}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Due Date</Label>
            <DatePicker
              className="w-full"
              format="MM/DD/YYYY"
              placeholder="mm/dd/yyyy"
              value={values.dueDate ? dayjs(values.dueDate) : null}
              onChange={(d) => setFieldValue('dueDate', d ? d.format('YYYY-MM-DD') : '')}
            />
          </div>
        </div>

        {/* Description */}
        <label className="flex flex-col gap-1.5">
          <Label>Task Description</Label>
          <textarea
            name="description"
            rows={4}
            placeholder="Provide comprehensive details, prerequisites, and expected outcomes for this task..."
            value={values.description}
            onChange={handleChange}
            onBlur={handleBlur}
            className={TEXTAREA}
          />
        </label>

        {/* Attachments */}
        <div className="flex flex-col gap-2">
          <Label>Attachments</Label>
          <label className="flex cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-[#cbd5e1] bg-white px-6 py-8 text-center transition-colors hover:border-brand/50">
            <ImagePlaceholderIcon className="size-12 text-[#94a3b8]" />
            <p className="pt-4 text-sm">
              <span className="font-semibold text-brand">Upload a file</span>
              <span className="text-[#475569]"> or drag and drop</span>
            </p>
            <p className="text-xs text-[#64748b]">PNG, JPG, PDF up to 10MB</p>
            <input type="file" accept=".png,.jpg,.jpeg,.pdf" className="hidden" />
          </label>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-6 border-t border-[#f1f5f9] pt-4">
          <button
            type="button"
            onClick={() => navigate(backPath, { state: backState })}
            className="text-sm font-semibold uppercase tracking-[0.35px] text-[#475569] transition-colors hover:text-ink"
          >
            CANCEL
          </button>
          <button
            type="submit"
            className="flex items-center gap-2 rounded-lg bg-brand px-6 py-2.5 text-sm font-semibold uppercase tracking-[0.35px] text-white shadow-btn transition-colors hover:bg-brand-dark"
          >
            <CreateTaskIcon className="h-[15px] w-[17px]" />
            {submitLabel}
          </button>
        </div>
      </div>
    </form>
  );
}
